import React from 'react'
import { Link } from 'react-router-dom'
import EventOutlinedIcon from '@material-ui/icons/EventOutlined'
import SecurityOutlinedIcon from '@material-ui/icons/SecurityOutlined'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'
import PhonelinkEraseOutlinedIcon from '@material-ui/icons/PhonelinkEraseOutlined'
import useSmsPolicyStyles from './smsPolicyStyles'

const collectedInfo = [
	'An authorized CCG user’s mobile telephone number',
	'SMS consent or opt-in status',
	'The date, time, and method of consent',
	'Records relating to SMS messages sent or delivered',
	'Records of opt-out and HELP requests',
	'Device, carrier, delivery-status, and error information',
	'Information provided during SMS-related support interactions',
]

const usageItems = [
	'Send one-time passcodes and identity-verification messages',
	'Provide security and account alerts',
	'Communicate assignment updates and status changes',
	'Send work-related reminders and operational notices',
	'Process opt-in, opt-out, and HELP requests',
	'Maintain the security and reliability of our systems',
	'Troubleshoot message-delivery issues',
	'Meet applicable compliance and recordkeeping obligations',
]

export default function SmsPrivacy() {
	const classes = useSmsPolicyStyles()

	return (
		<div className={classes.root}>
			<div className={classes.effectiveDate}>
				<div className={classes.effectiveIcon} aria-hidden="true">
					<EventOutlinedIcon />
				</div>
				<div>
					<p className={classes.effectiveTitle}>Effective Date: August 6, 2026</p>
					<p className={classes.effectiveBody}>
						This SMS Privacy Policy explains how Claim Consultant Group, LLC (“Claim Consultant Group,” “CCG,” “we,” “us,” or “our”) collects, uses, protects, and retains mobile telephone numbers, SMS consent information, and related messaging information for the CCG SMS program used by authorized CCG users (including employees and contractors).
					</p>
				</div>
			</div>

			<div className={classes.infoBanner} style={{ marginTop: 0, marginBottom: '2.75rem' }}>
				<InfoOutlinedIcon className={classes.infoIcon} aria-hidden="true" />
				<span>Message frequency varies. Message and data rates may apply. Reply STOP to cancel or HELP for help. We do not share, sell, or provide your mobile phone number or messaging consent data to third parties or affiliates for marketing or promotional purposes.</span>
			</div>

			<section className={classes.section}>
				<div className={classes.sectionHeader}>
					<div className={classes.sectionIcon} aria-hidden="true">
						<SecurityOutlinedIcon />
					</div>
					<div>
						<h2 className={classes.sectionTitle}>Overview</h2>
						<span className={classes.accent} aria-hidden="true" />
					</div>
				</div>
				<p className={classes.body}>
					Claim Consultant Group uses SMS text messaging to send one-time passcodes and non-marketing, work-related notifications to authorized CCG users (including employees and contractors) who have provided a mobile telephone number and consented to receive applicable SMS messages.
				</p>
				<p className={classes.body}>
					For program rules, messaging practices, and opt-out instructions, see the <Link to="/sms-terms">SMS Terms and Conditions</Link>. For a description of how consent is collected, see <Link to="/sms-opt-in">How You Opt In</Link>.
				</p>
				<div className={classes.infoBanner}>
					<InfoOutlinedIcon className={classes.infoIcon} aria-hidden="true" />
					<span>The Claim Consultant Group SMS program is not used to send advertising, marketing, sales, or promotional messages.</span>
				</div>
			</section>

			<section className={classes.section}>
				<div className={classes.sectionHeader}>
					<div className={classes.sectionIcon} aria-hidden="true">
						<LockOutlinedIcon />
					</div>
					<div>
						<h2 className={classes.sectionTitle}>Information We Collect</h2>
						<span className={classes.accent} aria-hidden="true" />
					</div>
				</div>
				<p className={classes.body}>In connection with the Claim Consultant Group SMS program, we may collect:</p>
				<ul className={classes.checkList}>
					{collectedInfo.map((item) => (
						<li key={item} className={classes.checkItem}>
							<CheckCircleIcon className={classes.checkIcon} aria-hidden="true" />
							<span>{item}</span>
						</li>
					))}
				</ul>
			</section>

			<section className={classes.section}>
				<h2 className={classes.sectionTitle}>How We Use the Information</h2>
				<span className={classes.accent} aria-hidden="true" />
				<p className={classes.body} style={{ marginTop: '1rem' }}>We may use this information to:</p>
				<ul className={classes.checkList}>
					{usageItems.map((item) => (
						<li key={item} className={classes.checkItem}>
							<CheckCircleIcon className={classes.checkIcon} aria-hidden="true" />
							<span>{item}</span>
						</li>
					))}
				</ul>
			</section>

			<section className={classes.legalSection}>
				<h2>How You Opt In</h2>
				<p>Authorized CCG users (including employees and contractors) opt in at <a href="https://www.claimconsultantgroup.com/sms-opt-in">https://www.claimconsultantgroup.com/sms-opt-in</a> by entering their mobile telephone number and affirmatively checking an unchecked consent box. This is the only opt-in method used for this campaign. Consent is collected specifically for this SMS program and is not bundled with any other agreement.</p>
				<p>The hosted enrollment form, screenshot, and exact consent language are published on our <Link to="/sms-opt-in">SMS Opt-In Process</Link> page.</p>
			</section>

			<div className={classes.splitRow}>
				<div className={classes.splitCol}>
					<div className={classes.splitIcon} aria-hidden="true">
						<LockOutlinedIcon />
					</div>
					<h2 className={classes.splitTitle}>Consent and Choice</h2>
					<span className={classes.accent} aria-hidden="true" />
					<p className={classes.body} style={{ marginTop: '1rem' }}>
						Participation is voluntary. Authorized CCG users must provide a valid mobile number and affirmatively consent before receiving applicable SMS messages.
					</p>
					<p className={classes.body}>
						SMS consent is separate from acceptance of other agreements or policies. Users who do not wish to receive SMS messages may contact Claim Consultant Group regarding available alternative methods.
					</p>
				</div>
				<div className={classes.splitDivider} aria-hidden="true" />
				<div className={classes.splitCol}>
					<div className={classes.splitIcon} aria-hidden="true">
						<PhonelinkEraseOutlinedIcon />
					</div>
					<h2 className={classes.splitTitle}>Opt-Out &amp; Help</h2>
					<span className={classes.accent} aria-hidden="true" />
					<p className={classes.body} style={{ marginTop: '1rem' }}>
						Reply <span className={classes.keyword}>STOP</span> to opt out of future SMS messages. After processing, no additional SMS messages will be sent unless the user later opts in again.
					</p>
					<p className={classes.body}>
						Reply <span className={classes.keyword}>HELP</span> for assistance, or contact <a href="mailto:info@claimconsultantgroup.com">info@claimconsultantgroup.com</a> / <a href="tel:8886451480">(888) 645-1480</a>.
					</p>
				</div>
			</div>

			<section className={classes.legalSection}>
				<h2>Non-Sharing of Mobile Information</h2>
				<p>We do not share, sell, or provide your mobile phone number or messaging consent data to third parties or affiliates for marketing or promotional purposes.</p>
				<p>No mobile information will be shared with third parties or affiliates for marketing or promotional purposes.</p>
				<p>Text messaging originator opt-in data and consent will not be shared with any third parties.</p>
				<p>We may provide limited mobile information to service providers, including telecommunications, messaging, hosting, security, and technology providers, only when reasonably necessary to operate, secure, support, and deliver the SMS program (for example, customer-service and message-delivery subcontractors). These service providers are not authorized to use mobile information for their own marketing or promotional purposes.</p>
				<p>Mobile information will not be transferred to external organizations for purposes unrelated to providing the SMS program, except when required by law or necessary to protect the security, rights, or property of Claim Consultant Group or others.</p>
			</section>

			<section className={classes.legalSection}>
				<h2>Message Frequency and Carrier Charges</h2>
				<p>Message frequency varies. Frequency depends on login activity, account-security events, assignments, status changes, reminders, and other work-related events.</p>
				<p>Message and data rates may apply. Carrier charges are determined by the user’s mobile-service plan; Claim Consultant Group is not responsible for charges imposed by a mobile carrier.</p>
			</section>

			<section className={classes.legalSection}>
				<h2>Data Security and Retention</h2>
				<p>Claim Consultant Group uses reasonable administrative, technical, and organizational safeguards designed to protect mobile information from unauthorized access, loss, misuse, alteration, or disclosure.</p>
				<p>No electronic transmission or storage method can be guaranteed to be completely secure. Mobile information is retained only for as long as reasonably necessary to operate and secure the SMS program, provide support, meet compliance and recordkeeping obligations, resolve disputes, and enforce applicable agreements.</p>
			</section>

			<section className={classes.legalSection}>
				<h2>Policy Updates</h2>
				<p>Claim Consultant Group may update this SMS Privacy Policy periodically to reflect changes in its messaging practices, technology, or legal and regulatory obligations.</p>
				<p>The current version will be posted on this webpage and identified by its effective date. Continued participation in the SMS program after an updated policy becomes effective will be governed by the updated policy, subject to applicable consent requirements.</p>
			</section>

			<section className={classes.legalSection}>
				<h2>Contact Us</h2>
				<p>Questions or concerns regarding this SMS Privacy Policy may be directed to:</p>
				<address>
					Claim Consultant Group, LLC<br />
					308 S. Meadowlark Street<br />
					Lakeway, Texas 78734<br />
					Email: <a href="mailto:info@claimconsultantgroup.com">info@claimconsultantgroup.com</a><br />
					Phone: <a href="tel:8886451480">(888) 645-1480</a>
				</address>
			</section>
		</div>
	)
}
